import { React, useState, useEffect, useRef } from 'react';
import PhotoPlaceholder from '../PagesClaude/placeholder';


const Lightbox = ({ id, items, route, onClose, onNav }) => {
	
	const idx = items.findIndex((p) => p.id === id);
	const p = items[idx];
	
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape") onClose();
			else if (e.key === "ArrowRight") onNav(1);
			else if (e.key === "ArrowLeft") onNav(-1);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose, onNav]);
	
	// Swipe / drag navigation on the image frame.
	const frameRef = useRef(null);
	const drag = useRef({ active: false, startX: 0, startY: 0, dx: 0, locked: null, pointerId: null });
	const [dx, setDx] = useState(0);
	const [snapping, setSnapping] = useState(false);
	
	const SWIPE_THRESHOLD = 60; // px to trigger a navigation
	
	const onPointerDown = (e) => {
		// Ignore non-primary buttons for mouse
		if (e.pointerType === "mouse" && e.button !== 0) return;
		drag.current.active = true;
		drag.current.startX = e.clientX;
		drag.current.startY = e.clientY;
		drag.current.dx = 0;
		drag.current.locked = null;
		drag.current.pointerId = e.pointerId;
		setSnapping(false);
		try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
	};
	
	const onPointerMove = (e) => {
		if (!drag.current.active) return;
		const dxRaw = e.clientX - drag.current.startX;
		const dyRaw = e.clientY - drag.current.startY;
		// Lock to horizontal vs vertical once movement exceeds a small threshold.
		if (drag.current.locked == null) {
			if (Math.abs(dxRaw) > 8 || Math.abs(dyRaw) > 8) {
				drag.current.locked = Math.abs(dxRaw) > Math.abs(dyRaw) ? "x" : "y";
			}
		}
		if (drag.current.locked !== "x") return;
		// Prevent the page from scrolling while we drag horizontally.
		if (e.cancelable) e.preventDefault();
		drag.current.dx = dxRaw;
		setDx(dxRaw);
	};
	
	const endDrag = (e) => {
		if (!drag.current.active) return;
		const finalDx = drag.current.dx;
		drag.current.active = false;
		try { e?.currentTarget?.releasePointerCapture?.(drag.current.pointerId); } catch (_) {}
		
		if (drag.current.locked === "x" && Math.abs(finalDx) > SWIPE_THRESHOLD) {
			// Animate the frame out in the swipe direction, then advance.
			const w = frameRef.current?.getBoundingClientRect().width || 800;
			const exit = finalDx > 0 ? w : -w;
			setSnapping(true);
			setDx(exit);
			window.setTimeout(() => {
				// Disable the snap transition so the new image appears instantly at 0,
				// then re-enable for any subsequent snaps.
				setSnapping(false);
				setDx(0);
				onNav(finalDx > 0 ? -1 : 1);
			}, 220);
		} else {
			// Snap back to center.
			setSnapping(true);
			setDx(0);
			window.setTimeout(() => setSnapping(false), 240);
		}
	};
	
	if (!p) return null;
	

	
	return (

		<div className="wi-lightbox" role="dialog" aria-modal="true">

			<header className="wi-lb-head">
				<div className="wi-mono wi-lb-no">
					{String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
				</div>
				<button className="wi-lb-close" onClick={onClose}>
					<span className="wi-mono">Close</span>
					<span aria-hidden>×</span>
				</button>
			</header>
			
			<div className="wi-lb-stage">
				<button
					className="wi-lb-arrow wi-lb-prev"
					onClick={() => onNav(-1)}
					aria-label="Previous plate"
				>
					<span aria-hidden>←</span>
				</button>
				
				<div
					className="wi-lb-frame"
					ref={frameRef}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={endDrag}
					onPointerCancel={endDrag}
					style={{
						touchAction: "pan-y",
						cursor: drag.current.active ? "grabbing" : "grab",
					}}
				>
					<div
						className="wi-lb-frame-slide"
						style={{
							transform: `translate3d(${dx}px, 0, 0)`,
							transition: snapping ? "transform 220ms ease" : "none",
							opacity: snapping && Math.abs(dx) > 100 ? 0 : 1,
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<PhotoPlaceholder
							src={p.cdnUrl}
							caption={p.caption}
							ratio={p.ratio}
							index={idx}
							tone="dark"
							showMeta={false}
							imgStyle={{
								objectFit: 'contain',
								filter: "none",
							}}
						/>
					</div>
				</div>
				
				<button
					className="wi-lb-arrow wi-lb-next"
					onClick={() => onNav(1)}
					aria-label="Next plate"
				>
					<span aria-hidden>→</span>
				</button>
			</div>
			
			<footer className="wi-lb-foot">
				<div className="wi-lb-meta">

					{ ( route === 'shop' ) && 
						<>
							<h2 className="wi-lb-title">
								{p.pricingsJson?.info || ''} — {p.pricingsJson?.price} €
							</h2>
							<div className="wi-lb-sub">
								<span hidden className="wi-dot-sep" aria-hidden>·</span>
								<span>{p.title || ''} · {p.country} · {p?.date_content.split('-')[2]}</span>
							</div>
						</>
					}

					{ ( route === 'gallery' ) && 
						<>
							<h2 className="wi-lb-title">
								{p.title || ''}
							</h2>
							<div className="wi-lb-sub">
								<span hidden className="wi-dot-sep" aria-hidden>·</span>
								<span className="">{p.country} · {p.date_content.split('-')[2]}</span>
							</div>
						</>
					}

					{ ( route === 'home' || route === 'about' ) && 
						<>
							<h2 className="wi-lb-title">{p.pricingsJson?.name || ''}</h2>
							{ p.pricingsJson?.info !== undefined && p.pricingsJson?.info !== '' && 
								<div className="wi-lb-sub">
									<span hidden className="wi-dot-sep" aria-hidden>·</span>
									<span className="">{p.pricingsJson?.info || ''}</span>
								</div>
							}
						</>
					}

				</div>

				{ (route === 'shop') && 
					<div>
						<span className="wi-mono rsp-sm-hidden">
							{p.pricingsJson?.name || ''} — {p.pricingsJson?.sku || ''}
							&nbsp;&nbsp;&nbsp;
						</span>
						<a href={p.pricingsJson.buyUrl} className="wi-btn-sm" target='_blank' rel="noreferrer">
							Buy <span aria-hidden>→</span>
						</a>
					</div>
				}

				<div className="wi-lb-keys wi-mono">
					<span><kbd>←</kbd><kbd>→</kbd> Navigate</span>
					<span><kbd>Esc</kbd> Close</span>
				</div>
			</footer>
		
		</div>

	);
};

export default Lightbox;
